"use client"

import { useState, useEffect, useCallback } from "react"
import { Card, Form, Row, Col, Button, Spinner, Alert, Badge, Table } from "react-bootstrap"
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts"
import { apiRequest, endpoints } from "../utils/api"
import { toast } from "react-hot-toast"
import HourlyWeatherChart from "./HourlyWeatherChart"
import * as XLSX from "xlsx"
import jsPDF from "jspdf"
import "jspdf-autotable"

const HourlySuitabilityReport = ({ location, selectedCrop }) => {
  const [suitabilityData, setSuitabilityData] = useState([])
  const [loading, setLoading] = useState(false)
  const [startDate, setStartDate] = useState(() => {
    const date = new Date()
    return date.toISOString().split("T")[0]
  })
  const [endDate, setEndDate] = useState(() => {
    const date = new Date()
    date.setDate(date.getDate() + 7)
    return date.toISOString().split("T")[0]
  })
  const [reportSummary, setReportSummary] = useState(null)

  const fetchHourlySuitability = useCallback(async () => {
    if (!location || !selectedCrop) return;

    setLoading(true);
    try {
      const response = await apiRequest(
        `${endpoints.crops}/hourly-suitability?lat=${location.lat}&lng=${location.lng}&cropId=${selectedCrop.id}&startDate=${startDate}&endDate=${endDate}`,
      );

      setSuitabilityData(response.hourlyData || []);
      setReportSummary(response.summary || null);
    } catch (error) {
      console.error("Error fetching hourly suitability:", error);
      toast.error("Failed to fetch hourly suitability data");
    } finally {
      setLoading(false);
    }
  }, [location, selectedCrop, startDate, endDate]);

  useEffect(() => {
    if (location && selectedCrop) {
      fetchHourlySuitability()
    }
  }, [location, selectedCrop, fetchHourlySuitability])

  const handleDateRangeUpdate = () => {
    fetchHourlySuitability()
  }

  const exportToExcel = () => {
    if (!suitabilityData.length) {
      toast.error("No data to export")
      return
    }

    const exportData = suitabilityData.map((item, index) => ({
      Time: item.time,
      "Suitability Score (%)": item.suitabilityScore,
      "Temperature (°C)": item.temperature,
      "Humidity (%)": item.humidity,
      "Soil Temperature (°C)": item.soilTemp,
      Conditions: item.conditions || "Normal",
    }))

    const ws = XLSX.utils.json_to_sheet(exportData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Hourly Suitability")

    const fileName = `hourly-suitability-${selectedCrop?.name || "crop"}-${startDate}-to-${endDate}.xlsx`
    XLSX.writeFile(wb, fileName)
    toast.success("Excel report exported successfully!")
  }

  const exportToPDF = () => {
    if (!suitabilityData.length) {
      toast.error("No data to export")
      return
    }

    const doc = new jsPDF()

    // Header
    doc.setFontSize(20)
    doc.text("Hourly Crop Suitability Report", 20, 20)

    doc.setFontSize(12)
    doc.text(`Crop: ${selectedCrop?.name || "Unknown"}`, 20, 35)
    doc.text(`Location: ${location?.name || `${location?.lat}, ${location?.lng}`}`, 20, 45)
    doc.text(`Period: ${startDate} to ${endDate}`, 20, 55)
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, 65)

    // Summary statistics
    if (reportSummary) {
      doc.setFontSize(14)
      doc.text("Summary Statistics", 20, 85)

      doc.setFontSize(10)
      doc.text(`Optimal Hours (≥80%): ${reportSummary.optimalHours}`, 20, 95)
      doc.text(`Good Hours (60-79%): ${reportSummary.goodHours}`, 20, 105)
      doc.text(`Average Score: ${reportSummary.averageScore}%`, 20, 115)
      doc.text(`Best Time: ${reportSummary.bestTime}`, 20, 125)
    }

    // Table data
    const tableData = suitabilityData.map((item) => [
      item.time,
      `${item.suitabilityScore}%`,
      `${item.temperature}°C`,
      `${item.humidity}%`,
      `${item.soilTemp}°C`,
      item.conditions || "Normal",
    ])

    doc.autoTable({
      head: [["Time", "Suitability", "Temperature", "Humidity", "Soil Temp", "Conditions"]],
      body: tableData,
      startY: reportSummary ? 140 : 80,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [40, 167, 69] },
    })

    const fileName = `hourly-suitability-${selectedCrop?.name || "crop"}-${startDate}-to-${endDate}.pdf`
    doc.save(fileName)
    toast.success("PDF report exported successfully!")
  }

  const getSuitabilityBadge = (score) => {
    if (score >= 80) return "success"
    if (score >= 60) return "warning"
    if (score >= 40) return "info"
    return "danger"
  }

  return (
    <div className="mb-4">
      <Card className="shadow-sm">
        <Card.Header className="bg-white">
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">🌱 Hourly Crop Suitability Report</h5>
              {selectedCrop && <small className="text-muted">Analysis for {selectedCrop.name}</small>}
            </Col>
            <Col xs="auto">
              <Row className="g-2 align-items-center">
                <Col>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Col>
                <Col>
                  <Form.Control type="date" size="sm" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </Col>
                <Col>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={handleDateRangeUpdate}
                    disabled={loading || !selectedCrop}
                  >
                    {loading ? <Spinner size="sm" /> : "Generate Report"}
                  </Button>
                </Col>
                {suitabilityData.length > 0 && (
                  <>
                    <Col>
                      <Button size="sm" variant="success" onClick={exportToExcel} title="Export to Excel">
                        📊 Excel
                      </Button>
                    </Col>
                    <Col>
                      <Button size="sm" variant="danger" onClick={exportToPDF} title="Export to PDF">
                        📄 PDF
                      </Button>
                    </Col>
                  </>
                )}
              </Row>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {!selectedCrop ? (
            <Alert variant="info">
              <Alert.Heading>Select a Crop</Alert.Heading>
              <p>Please select a crop from the suitable crops table above to generate an hourly suitability report.</p>
            </Alert>
          ) : loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Generating hourly suitability report...</p>
            </div>
          ) : suitabilityData.length > 0 ? (
            <>
              {reportSummary && (
                <Row className="mb-4">
                  <Col md={3}>
                    <Card className="text-center border-success">
                      <Card.Body>
                        <h6 className="text-success">Optimal Hours</h6>
                        <h4 className="text-success">{reportSummary.optimalHours}</h4>
                        <small className="text-muted">≥80% suitability</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center border-warning">
                      <Card.Body>
                        <h6 className="text-warning">Good Hours</h6>
                        <h4 className="text-warning">{reportSummary.goodHours}</h4>
                        <small className="text-muted">60-79% suitability</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center border-info">
                      <Card.Body>
                        <h6 className="text-info">Average Score</h6>
                        <h4 className="text-info">{reportSummary.averageScore}%</h4>
                        <small className="text-muted">Overall period</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center border-primary">
                      <Card.Body>
                        <h6 className="text-primary">Best Time</h6>
                        <h6 className="text-primary">{reportSummary.bestTime}</h6>
                        <small className="text-muted">Peak suitability</small>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              <div className="mb-4">
                <h6>Hourly Suitability Trend</h6>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={suitabilityData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip
                      labelFormatter={(value) => `Time: ${value}`}
                      formatter={(value, name) => [`${value}%`, name]}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="suitabilityScore"
                      stroke="#007bff"
                      strokeWidth={3}
                      name="Suitability Score"
                      dot={{ fill: "#007bff", strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="mb-4">
                <h6>Detailed Hourly Analysis</h6>
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  <Table striped hover size="sm">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>Time</th>
                        <th>Suitability</th>
                        <th>Temperature</th>
                        <th>Humidity</th>
                        <th>Soil Temp</th>
                        <th>Conditions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suitabilityData.map((item, index) => (
                        <tr key={index}>
                          <td>{item.time}</td>
                          <td>
                            <Badge bg={getSuitabilityBadge(item.suitabilityScore)}>{item.suitabilityScore}%</Badge>
                          </td>
                          <td>{item.temperature}°C</td>
                          <td>{item.humidity}%</td>
                          <td>{item.soilTemp}°C</td>
                          <td>
                            <small className={`text-${getSuitabilityBadge(item.suitabilityScore)}`}>
                              {item.conditions || "Normal"}
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-5 text-muted">
              <p>No hourly suitability data available</p>
              <small>Try selecting a different date range or crop</small>
            </div>
          )}
        </Card.Body>
      </Card>

      {location && (
        <div className="mt-4">
          <HourlyWeatherChart location={location} />
        </div>
      )}
    </div>
  )
}

export default HourlySuitabilityReport
